import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../../aura';
import { FormContextData, useFormContext } from '../../state';
import { BUSINESS_NAME_MAX_LENGTH, isBusinessNameTooLong } from '../../helpers';
import { useValidation } from './helpers/use-validation';
import { useFormErrors } from './helpers/use-form-errors';

export function BusinessName(): JSX.Element {
  const { t } = useTranslation('input', { keyPrefix: 'businessName' });
  const {
    data: { businessName },
    setData,
    clearState,
  } = useFormContext();

  const hasValue = useCallback(
    (data: FormContextData) =>
      data.businessName.trim() ? null : 'BusinessName is empty',
    [],
  );

  const validate = useCallback((data: FormContextData) => {
    if (!data.businessName.trim()) {
      return 'BusinessName is empty';
    }
    if (isBusinessNameTooLong(data.businessName.trim())) {
      return 'BusinessName is too long';
    }
    return null;
  }, []);

  const extraInputProps = useValidation(t, hasValue, validate);
  // Availability ("name taken") is checked on Next, not here — see the STEP_ONE_FORM case in
  // step-machine. The error it raises is surfaced through this same useFormErrors hook.
  const { props: formErrorsProps, clear } = useFormErrors('businessName');

  const onValueChange = useCallback(
    (value: string) => {
      clear();
      clearState();
      setData(current => ({ ...current, businessName: value }));
    },
    [clear, clearState, setData],
  );

  return (
    <Input
      id="business-name"
      type="text"
      label={t('label')}
      value={businessName}
      onValueChange={onValueChange}
      maxLength={BUSINESS_NAME_MAX_LENGTH}
      {...extraInputProps}
      {...formErrorsProps}
    />
  );
}
